/* eslint-disable jsx-a11y/anchor-is-valid */
import qs from "qs";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useApiOperation } from "src/api/hooks";
import { addOrg, editOrg, getOrgDetail } from "src/api/organization/org-api";
import { BizType, SaveMediaDto, SendBody, TaxType } from "src/api/organization/org-types";
import { MediaFile, Select } from "src/api/public-types";
import { BaseButton, BaseCheckbox, BaseInput, BaseModal, BaseRadio, BaseSelect, BaseTooltip } from "src/components";
import BaseImageUpload, { BaseImageFile } from "src/components/BaseImageUpload";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import EditCancelButton from "src/components/EditCancelButton";
import GoToListButton from "src/components/GoToListButton";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import useNavigate from "src/hooks/usePartnerNavigate";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import PagePath from "src/pagePath.json";
import { formatPhoneNumber, INVALID_PHONE_NUMBER_MESSAGE, isValidKoreanMobile, parsedPhoneNumber } from "src/utils";
import { companyRegistrationHypen, corpHypen, removeHyphens } from "src/utils/common-util";

const individualTaxOption = [
  { value: TaxType.GENERAL, label: "일반" },
  { value: TaxType.SIMPLIFIED_A, label: "간이(세금계산서 발급형)" },
  { value: TaxType.SIMPLIFIED_B, label: "간이(세금계산서 미발급형)" },
  { value: TaxType.FREE, label: "면세" },
];
const corpTaxOption = [
  { value: TaxType.GENERAL, label: "과세" },
  { value: TaxType.FREE, label: "면세" },
];

const checkBizRegNoLink = "https://teht.hometax.go.kr/websquare/websquare.html?w2xPath=/ui/ab/a/a/UTEABAAA13.xml";

const BasicInfoForm = ({ setOrgData }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthority } = usePartnerAuthority();
  let id = 0;
  const querys = qs.parse(location.search);
  for (const [key, value] of Object.entries(querys)) {
    if (key.includes("id")) {
      id = Number(value);
    }
  }

  const [moveId, setMoveId] = useState<number>(0);
  const [privateFiles, setPrivateFiles] = useState<BaseImageFile[]>([]);
  const [uploadMediaFile, setUploadedMediaFile] = useState<BaseImageFile[]>([]);
  const [uploadFile, setUploadeFile] = useState<BaseImageFile[]>([]);
  const [copRegNoFlag, setCopRegNoFlag] = useState(false);
  const [openFlag, setOpenFlag] = useState(false);
  const [confirmFlag, setConfirmFlag] = useState(false);
  const [taxOption, setTaxOption] = useState<Select[]>(individualTaxOption);
  const { executeAsync: addOrganization } = useApiOperation(addOrg);
  const { executeAsync: getDetail } = useApiOperation(getOrgDetail);
  const { executeAsync: editOrganization } = useApiOperation(editOrg);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    watch,
    trigger,
    formState: { errors, dirtyFields },
  } = useForm<any>({
    defaultValues: {
      bizLocationType: "DOMESTIC",
      address: "",
      bizCondition: "",
      bizItem: "",
      bizRegNo: "",
      bizType: BizType.SP,
      ceoName: "",
      corRegNo: "",
      name: "",
      taxType: TaxType.GENERAL,
      phoneNumber: null,
      email: null,
      mediaList: [],
      classifications: ["GENERAL_BIZ"],
      partnerId: "",
    },
  });
  // 모든 필드를 한 번만 등록하고, 조건부 validation 적용
  useEffect(() => {
    const requiredMessage = "필수 입력 항목입니다";

    // 모든 필드를 미리 등록 (조건부 validation과 함께)
    register("ceoName", { required: requiredMessage });
    register("address", { required: requiredMessage });
    register("mediaList", { required: requiredMessage });

    // GENERAL_BIZ 관련 필드들
    register("name", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("GENERAL_BIZ")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });

    register("bizRegNo", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("GENERAL_BIZ")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });

    register("bizCondition", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("GENERAL_BIZ")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });

    register("bizItem", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("GENERAL_BIZ")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });


    // INDIVIDUAL 관련 필드들
    register("phoneNumber", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("INDIVIDUAL")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });

    register("email", {
      validate: (value) => {
        const currentClassifications = getValues("classifications");
        if (currentClassifications?.includes("INDIVIDUAL")) {
          return value ? true : requiredMessage;
        }
        return true;
      },
    });
  }, [copRegNoFlag]);

  const moveOrgDetail = useCallback(
    (id: number) => {
      const path = PagePath.organization.detail?.replace(":id", id!.toString());
      navigate(path);
    },
    [navigate],
  );
  //사업자/법인 등록번호 검증
  const checkValidateOrgNumber = useCallback((number: string) => {
    if (!number) return false;

    const cleanNumber = number?.replace(/-/gi, "");
    const numberMap: number[] = cleanNumber.split("").map((d) => parseInt(d, 10));

    // 10자리(사업자등록번호) 또는 13자리(법인등록번호) 검증
    if (numberMap.length === 10 || numberMap.length === 13) {
      return true;
    }

    return false;
  }, []);

  const getDetailData = useCallback(() => {
    if (id) {
      getDetail({ id: Number(id) }).then(async (response) => {
        if (response.data.data.organization) {
          const og: any = response.data.data.organization;
          if (og.bizType === BizType.SP) {
            setCopRegNoFlag(false);
            setTaxOption(individualTaxOption);
            setValue("taxType", "");
            setValue("taxType", og.taxType);
          } else {
            setCopRegNoFlag(true);
            setTaxOption(corpTaxOption);
            setValue("taxType", og.taxType);
          }

          setValue("address", og.address);
          setValue("bizCondition", og.bizCondition);
          setValue("bizLocationType", og.bizLocationType || "DOMESTIC");
          setValue("bizRegNo", companyRegistrationHypen(og.bizRegNo));
          setValue("bizItem", og.bizItem);
          setValue("bizType", og.bizType);
          setValue("ceoName", og.ceoName);
          setValue("corRegNo", corpHypen(og.corRegNo));
          setValue("name", og.name);
          setValue("email", og.email);
          setValue("phoneNumber", formatPhoneNumber(og.phoneNumber));
          setValue("classifications", og.classifications);
          setValue("partnerId", og.partnerId);
        }

        if (response.data.data.mediaList && response.data.data.mediaList.length > 0) {
          const logos: any = response.data.data.mediaList.filter((md: any) => md.type === "LOGO");
          const proofs: any = response.data.data.mediaList.filter((md: any) => md.type === "PROOF");
          setPrivateFiles(proofs);
          setUploadedMediaFile(logos);
          setUploadeFile(logos);
        }
        setOrgData(response.data.data);
      });
    }
  }, [getDetail, id, setOrgData, setValue]);

  useEffect(() => {
    getDetailData();
  }, [getDetailData]);

  const sendAddOrg = useCallback(
    async (org: SendBody) => {
      const { data } = await addOrganization(org);
      if (data.data.organization.id) {
        setMoveId(data.data.organization.id);
        setOpenFlag(true);
      }
    },
    [addOrganization],
  );

  const sendEditOrg = useCallback(
    (org: SendBody) => {
      if (org) {
        editOrganization(org).then((response: any) => {
          setMoveId(response.data.data.organization.id);
          setOpenFlag(true);
        });
      }
    },
    [editOrganization],
  );

  const onSubmit = useCallback(
    async (data: any, e?: any) => {
      // 법인사업자일 때 법인 등록 번호 유효성 검사
      if (copRegNoFlag) {
        const corRegNo = getValues("corRegNo");
        if (corRegNo) {
          const cleanNumber = corRegNo.replace(/-/g, "");
          if (cleanNumber.length !== 13) {
            setError("corRegNo", {
              type: "custom",
              message: "유효한 법인 등록 번호가 아닙니다.",
            });
            return;
          }
        }
      }

      const emailCheck = getValues("email");
      if (emailCheck !== null && emailCheck !== "") {
        if (emailCheck !== undefined && emailCheck !== "") {
          const pattern = /^[\w-\.\+]+@([\w-]+\.)+[\w-]{2,4}$/g;

          if (pattern.test(emailCheck) === false) {
            setError("email", {
              type: "custom",
              message: "올바르지 않은 이메일 형식입니다.",
            });
            return;
          }
        }
      }

      const phoneCheck = getValues("phoneNumber");
      // 법인은 이 필드가 선택 입력이고, 과거 검증이 느슨해 유선번호가 저장된 레코드가 있을 수 있다.
      // 손대지 않은 기존 값 때문에 무관한 필드 수정이 막히지 않도록 사용자가 수정했을 때만 검증한다.
      if (phoneCheck && dirtyFields.phoneNumber) {
        if (isValidKoreanMobile(phoneCheck) === false) {
          setError("phoneNumber", {
            type: "custom",
            message: INVALID_PHONE_NUMBER_MESSAGE,
          });
          return;
        }
      }

      let orgData = { ...data };

      e.preventDefault();
      if (id) {
        orgData.id = Number(id);
      }

      orgData.address = data.address;
      orgData.bizCondition = data.bizCondition;
      orgData.bizItem = data.bizItem;
      orgData.bizRegNo = removeHyphens(data.bizRegNo);
      orgData.bizType = data.bizType;
      orgData.ceoName = data.ceoName;
      orgData.corRegNo = removeHyphens(data.corRegNo);
      orgData.name = data.name;
      orgData.classifications = data.classifications;
      orgData.ceoName = data.ceoName;
      if (data.email !== "") {
        orgData.email = data.email;
      }
      if (data.phoneNumber) {
        orgData.phoneNumber = parsedPhoneNumber(data.phoneNumber);
      } else {
        orgData.phoneNumber = null;
      }

      if (getValues("classifications").includes("INDIVIDUAL")) {
        const { bizRegNo, bizType, taxType, bizCondition, bizItem, corRegNo, mediaList, name, partnerId, ...cleanedData } = orgData;
        orgData = cleanedData;
      }
      if (getValues("classifications").includes("GENERAL_BIZ")) {
        const { mediaList, partnerId, ...cleanedData } = orgData;
        orgData = cleanedData;
      }
      const saveModel: SendBody = {
        organization: orgData,
        mediaList: uploadMediaFile as SaveMediaDto[],
      };
      saveModel.organization = orgData;
      saveModel.mediaList = [...privateFiles, ...uploadMediaFile] as SaveMediaDto[];

      if (id) {
        sendEditOrg(saveModel);
      } else {
        sendAddOrg(saveModel);
      }

      e.target.reset();

      return false;
    },
    [copRegNoFlag, dirtyFields, getValues, id, privateFiles, sendAddOrg, sendEditOrg, setError, uploadMediaFile],
  );

  // validation 통과하지 못하고 error 발생시 실행
  const onError = (errors: any, e?: any) => {
    e.preventDefault();

    return false;
  };

  const moveList = () => {
    if (id) {
      setConfirmFlag(true);
    } else {
      goList();
    }
  };

  const goList = useCallback(() => {
    const path = PagePath.organization.list;
    navigate(path);
  }, [navigate]);

  useEffect(() => {
    setValue("mediaList", privateFiles);
  }, [privateFiles, setValue]);

  //사업자 등록/ 수정 폼
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="contents-container__scroll">
          <div className="contents-container__wrap">
            <article className="contents-container__wrap-article">
              <BaseSectionTitle title={"상세 정보"} noHashLink />
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <div className="flex-center">
                    <p className="required">구분</p>
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="">
                    <div className="flex-center h-36">
                      <Controller
                        control={control}
                        name="classifications"
                        render={({ field: { onChange, value, name } }) => (
                          <div className="d-flex flex-center">
                            <BaseRadio
                              name={name}
                              id={"GENERAL_BIZ"}
                              value={"GENERAL_BIZ"}
                              checked={value?.includes("GENERAL_BIZ")}
                              onChange={() => {
                                onChange(["GENERAL_BIZ"]);
                                setValue("bizType", "SP");
                              }}
                              label={"사업자"}
                              className="mr10 d-flex flex-center"
                            />
                            <p className="font15 mr2">{`(`}</p>
                            <BaseCheckbox
                              id={`OWNER`}
                              name={`OWNER`}
                              checked={value?.includes("OWNER")}
                              onChange={(checked: boolean) => {
                                if (checked) {
                                  setValue("classifications", ["GENERAL_BIZ", "OWNER"]);
                                  trigger("classifications");
                                } else {
                                  setValue("classifications", ["GENERAL_BIZ"]);
                                  trigger("classifications");
                                }
                              }}
                              label={"소유주"}
                              className="d-flex flex-center"
                              disabled={watch("classifications").includes("INDIVIDUAL")}
                            />
                            <BaseTooltip contents="필요에 따라 ‘소유주' 항목을 함께 선택하면, 건물의 소유주로 등록할 수 있습니다." touchIcon="QUESTION" />
                            <p className="font15 mr15 ml2">{`)`}</p>
                            <BaseRadio
                              name={name}
                              id={"INDIVIDUAL"}
                              value={"INDIVIDUAL"}
                              checked={value?.includes("INDIVIDUAL")}
                              onChange={() => {
                                onChange(["INDIVIDUAL"]);
                              }}
                              label={"개인"}
                            />
                          </div>
                        )}
                      ></Controller>
                    </div>
                  </div>
                  {errors.classifications && <p className="validation-text">{String(errors?.classifications?.message)}</p>}
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">국가</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax200">
                    <Controller
                      control={control}
                      name="bizLocationType"
                      render={({ field: { onChange, value, name } }) => (
                        <div className="d-flex">
                          <BaseRadio
                            name={name}
                            id={"DOMESTIC"}
                            value={"DOMESTIC"}
                            checked={value === "DOMESTIC" ? true : false}
                            onChange={() => {
                              onChange("DOMESTIC");
                            }}
                            label={"국내"}
                            className="mr10"
                          />
                          <BaseRadio
                            name={name}
                            id={"OVERSEAS"}
                            value={"OVERSEAS"}
                            checked={value === "OVERSEAS" ? true : false}
                            onChange={() => {
                              onChange("OVERSEAS");
                            }}
                            label={"해외"}
                          />
                        </div>
                      )}
                    ></Controller>
                  </div>
                </div>
              </section>
              {watch("classifications").includes("GENERAL_BIZ") && (
                <>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">사업자 유형</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="bizType"
                          render={({ field: { onChange, value, name } }) => (
                            <div className="d-flex">
                              <BaseRadio
                                name={name}
                                id={"SP"}
                                value={"SP"}
                                checked={value === "SP" ? true : false}
                                onChange={() => {
                                  onChange("SP");
                                  setCopRegNoFlag(false);
                                  setTaxOption(individualTaxOption);
                                  setValue("taxType", individualTaxOption[0].value);
                                }}
                                label={"개인사업자"}
                                className="mr10"
                              />
                              <BaseRadio
                                name={name}
                                id={"CORP"}
                                value={"CORP"}
                                checked={value === "CORP" ? true : false}
                                onChange={() => {
                                  onChange("CORP");
                                  setCopRegNoFlag(true);
                                  setTaxOption(corpTaxOption);
                                  setValue("taxType", corpTaxOption[0].value);
                                }}
                                label={"법인사업자"}
                              />
                            </div>
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">사업자 등록 번호</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="bizRegNo"
                          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <BaseInput
                              name={name}
                              value={value}
                              maxLength={12}
                              placeholder={"번호를 입력해 주세요."}
                              onChange={(value: string) => {
                                onChange(companyRegistrationHypen(value));
                              }}
                              errorText={error?.message}
                            />
                          )}
                        ></Controller>
                      </div>
                      {watch("bizLocationType") === "DOMESTIC" && (
                        <p className="flex-center">
                          <ul className="base-list mr4"><li>국내 사업자 등록번호를 조회할 수 있습니다.</li></ul>

                          <BaseNewTabLink
                            path={checkBizRegNoLink}
                            className="ml10 text-underline"
                            value="국내 사업자 등록 번호 조회하기"
                          />
                        </p>
                      )}
                    </div>
                  </section>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">법인명/상호</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="name"
                          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <BaseInput
                              placeholder={"이름을 입력해 주세요."}
                              name={name}
                              value={value}
                              onChange={onChange}
                              errorText={error?.message}
                            />
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>


                </>
              )}

              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">{watch("classifications").includes("INDIVIDUAL") ? "이름" : "대표자/성명"}</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax200">
                    <Controller
                      control={control}
                      name="ceoName"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                        <BaseInput placeholder={"이름을 입력해 주세요."} name={name} value={value} onChange={onChange} errorText={error?.message} />
                      )}
                    ></Controller>
                  </div>
                </div>
              </section>
              {copRegNoFlag && (
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>법인 등록 번호</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="minmax200">
                      <Controller
                        control={control}
                        name="corRegNo"
                        rules={{
                          validate: (value) => {
                            if (!value) return true;
                            const cleanNumber = value.replace(/-/g, "");
                            return cleanNumber.length === 13 || "유효한 법인 등록 번호가 아닙니다.";
                          },
                        }}
                        render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                          <BaseInput
                            name={name}
                            value={value}
                            maxLength={14}
                            placeholder="번호를 입력해주세요."
                            onChange={(value: string) => {
                              onChange(corpHypen(value));
                            }}
                            onBlur={() => {
                              trigger("corRegNo");
                            }}
                            errorText={error?.message}
                          />
                        )}
                      ></Controller>
                    </div>
                  </div>
                </section>
              )}
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">{watch("classifications").includes("INDIVIDUAL") ? "주소" : "사업장 주소"}</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="w-100">
                    <Controller
                      control={control}
                      name="address"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                        <BaseInput placeholder={"주소를 입력해 주세요."} name={name} value={value} onChange={onChange} errorText={error?.message} />
                      )}
                    ></Controller>
                  </div>
                </div>
              </section>
              {watch("classifications").includes("GENERAL_BIZ") && (
                <>
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">업태</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="bizCondition"
                          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <BaseInput
                              placeholder={"업태를 입력해 주세요."}
                              name={name}
                              value={value}
                              onChange={onChange}
                              errorText={error?.message}
                            />
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                  {/* 종목 */}
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">종목</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="bizItem"
                          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <BaseInput
                              placeholder={"종목을 입력해 주세요."}
                              name={name}
                              value={value}
                              onChange={onChange}
                              errorText={error?.message}
                            />
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                  {/* 과세 유형 */}
                  <section className="contents-container__grid">
                    <div className="contents-container__grid-index">
                      <p className="required">과세 유형</p>
                    </div>
                    <div className="contents-container__grid-contents">
                      <div className="minmax200">
                        <Controller
                          control={control}
                          name="taxType"
                          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                            <>
                              <div className="d-flex">
                                {taxOption.map((option) => (
                                  <BaseRadio
                                    key={option.value}
                                    name={name}
                                    id={String(option.value)}
                                    value={option.value}
                                    checked={value === option.value}
                                    onChange={() => onChange(option.value)}
                                    label={option.label}
                                    className="mr10"
                                  />
                                ))}
                              </div>
                              {error && <p className="validation-text">{error.message}</p>}
                            </>
                          )}
                        ></Controller>
                      </div>
                    </div>
                  </section>
                </>
              )}

              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={watch("classifications").includes("INDIVIDUAL") ? "required" : ""}>휴대폰</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax200">
                    <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                        <BaseInput
                          placeholder={"휴대폰 번호를 입력해 주세요."}
                          type={"phone"}
                          name={name}
                          value={value}
                          onChange={onChange}
                          errorText={error?.message}
                        />
                      )}
                    ></Controller>
                  </div>
                </div>
              </section>

              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className={watch("classifications").includes("INDIVIDUAL") ? "required" : ""}>이메일</p>
                </div>
                <div className="contents-container__grid-contents">
                  <div className="minmax200">
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                        <BaseInput
                          placeholder={"이메일을 입력해 주세요."}
                          name={name}
                          value={value}
                          onChange={onChange}
                          errorText={error?.message}
                        />
                      )}
                    ></Controller>
                  </div>
                </div>
              </section>
              {/* 사업자 증빙 서류*/}
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <div className="d-flex flex-center">
                    <p className="required">
                      증빙 서류 {privateFiles.length > 9 && <br />}(
                      {privateFiles.length}/10)
                    </p>
                    {watch("classifications").includes("INDIVIDUAL") ? (
                      <BaseTooltip touchIcon={"QUESTION"} className="mt1" size={18}>
                        <ul className="pl12">
                          <li className="mb14">
                            신분증 사본 업로드 시, 주민등록번호·여권번호·외국인등록번호 등 고유식별정보는 반드시 마스킹(*) 처리해 주세요.
                          </li>
                          <li> 마스킹되지 않은 개인정보가 포함된 경우, 업로드가 제한되거나 법적 책임이 발생할 수 있습니다.</li>
                        </ul>
                      </BaseTooltip>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div className="contents-container__grid-contents">
                  <Controller
                    control={control}
                    name="mediaList"
                    render={({ field: { onChange }, fieldState: { error } }) => (
                      <>
                        <BaseImageUpload
                          images={privateFiles}
                          type="PROOF"
                          maxWidth={925}
                          setImages={(file: any) => {
                            setPrivateFiles(file);
                            onChange(file);
                          }}
                        />
                        <p className="validation-text">{error?.message}</p>
                      </>
                    )}
                  ></Controller>
                </div>
              </section>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="">로고 이미지 ({uploadMediaFile.length}/1)</p>
                </div>
                <BaseImageUpload
                  type="LOGO"
                  accept={".jpg, .png, .gif"}
                  fileMaxLength={1}
                  images={uploadMediaFile}
                  setImages={setUploadedMediaFile}
                />
              </section>
            </article>

            {openFlag && (
              <BaseModal title="저장 되었습니다." isOpen={openFlag} btnRightTitle="확인" onClick={() => moveOrgDetail(moveId!)}></BaseModal>
            )}
            {confirmFlag && (
              <BaseModal
                title="현재 페이지에서 나가시겠습니까?"
                isOpen={confirmFlag}
                btnLeftTitle="머무르기"
                btnRightTitle="나가기"
                onClick={() => goList()}
                onClose={() => setConfirmFlag(false)}
              >
                <div>
                  <span>
                    입력 중인 내용이 있는 경우,
                    <br /> 저장을 하지 않은 정보는 사라집니다.
                  </span>
                </div>
              </BaseModal>
            )}
          </div>
        </div>

        {/* 버튼영역 */}
        <div className="contents-container__btn-wrap">
          <div className="left-area"></div>
          <div className="right-area">
            <EditCancelButton to={id > 0 ? PagePath.organization.detail?.replace(":id", String(id)) : PagePath.organization.list} />
            {isAuthority("w") && <BaseButton title="저장" type={"submit"} className="size-large " />}
          </div>
        </div>
      </form>
    </>
  );
};

export default BasicInfoForm;
